import { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { UseFormReturn } from 'react-hook-form';
import { IForms } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import { IDocumentData } from '@models/document/IDocumentData';
import { IAttachesCreateModel } from '@models/attaches/IAttachesCreateModel';
import { IMailRemindersBlockModel } from '@models/mailRemindersBlock/IMailRemindersBlockModel';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { AttachActionsService } from '@services/actions/AttachActionsService';
import { RemindersService } from '@services/RemindersService';
import { convertListToMap, getEmptyFormDataByScheme } from '@utils/documentUtils';
import { VariablesManager } from '@utils/managers/VariablesManager';

export interface IBatchEditingFormDataOptions {
    docId: string;
    actionKey: string;
    /** Ид активити: у действия документа его нет. */
    activityId?: string;
    attachFiles?: boolean;
    addMailReminders?: boolean;
    showCurrentValues?: boolean;
    virtualFields?: string[];
    methods: UseFormReturn<IDocumentData>;
    getFormBuilderScheme: () => Promise<AxiosResponse<IForms>>;
    /** Текущие значения полей: параметры запроса у действия активити и документа разные. */
    getDataByAction: () => Promise<AxiosResponse<IDocumentData>>;
    onError: (text?: string) => void;
}

export interface IBatchEditingFormData {
    form?: IForms;
    fieldsData?: Record<string, IFieldElem>;
    attachesCreateModel?: IAttachesCreateModel;
    mailRemindersBlockModel?: IMailRemindersBlockModel;
}

/** Загрузка модалки пакетного редактирования: модель вложений, блок напоминаний,
 *  схема формы и её данные (пустые по схеме либо текущие значения документа). */
export function useBatchEditingFormData(options: IBatchEditingFormDataOptions): IBatchEditingFormData {
    const [form, setForm] = useState<IForms>();
    const [fieldsData, setFieldsData] = useState<Record<string, IFieldElem>>();
    const [attachesCreateModel, setAttachesCreateModel] = useState<IAttachesCreateModel>();
    const [mailRemindersBlockModel, setMailRemindersBlockModel] = useState<IMailRemindersBlockModel>();
    const user = useTypedSelector((state) => state.auth.user);

    useEffect(() => {
        const { docId, actionKey, activityId, methods } = options;
        // Закрытое окно ответы подготовки не применяет.
        let ignore = false;
        const onError = (text?: string) => {
            if (!ignore) options.onError(text);
        };

        // Функция для получения модели аттачей
        async function fetchAttachModel() {
            if (options.attachFiles) {
                const attachService = new AttachActionsService(docId, '-1');
                const res = await attachService.getAttachInfo(actionKey, activityId);
                if (!ignore) setAttachesCreateModel(res.data);
            }
        }

        // Функция для получения модели блока напоминаний
        async function fetchMailRemindersBlockModel() {
            if (options.addMailReminders) {
                const res = await RemindersService.getMailRemindersBlockModel(docId, actionKey, activityId);
                if (!ignore) setMailRemindersBlockModel(res.data);
            }
        }

        // Функция для получения схемы
        async function fetchFormBuilderScheme() {
            try {
                const dto = await options.getFormBuilderScheme();
                const form = dto.data;
                if (!ignore) setForm(form);

                if (!options.showCurrentValues) {
                    const formData = getEmptyFormDataByScheme(form?.view?.rows?.row, form.changeActions);
                    const fields = convertListToMap(formData.fields);

                    return { fields, formData, form };
                }
                return { form };
            } catch (error) {
                onError((error as Error)?.message);
            }
        }

        // Функция для получения данных
        async function fetchDataByAction() {
            if (options.showCurrentValues) {
                try {
                    const result = await options.getDataByAction();
                    const formData = result.data;

                    if (formData) {
                        const f = result.data.fields;

                        // Добавление виртуальных полей
                        if (options.virtualFields) {
                            options.virtualFields.forEach((x) => {
                                f.push({
                                    name: x,
                                    value: undefined,
                                });
                            });
                        }

                        const fields = convertListToMap(f);

                        return { fields, formData };
                    }
                } catch (error) {
                    onError((error as Error)?.message);
                }
            }
        }

        async function fetchData() {
            await fetchAttachModel();
            await fetchMailRemindersBlockModel();
            const formResult = await fetchFormBuilderScheme();
            const dataResult = await fetchDataByAction();

            const formData = dataResult?.formData ?? formResult?.formData;
            const form = formResult?.form;
            if (formData && form) {
                const existing = new Set(formData.fields.map((field) => field.name));
                const defaults = getEmptyFormDataByScheme(form?.view?.rows?.row, form.changeActions);
                formData.fields.push(...defaults.fields.filter((field) => !existing.has(field.name)));
            }
            const fields = formData ? convertListToMap(formData.fields) : undefined;

            // добавляем Variables
            if (fields) {
                const mng = new VariablesManager(
                    form?.view?.variables ?? { variable: [], dictVariable: [] },
                    docId,
                    user,
                );
                const variables = await mng.GetData(fields, true, false);
                fields['variables'] = {
                    name: 'variables',
                    value: variables,
                    index: -1,
                };
            }

            if (ignore) return;
            setFieldsData(fields);
            methods.reset(formData);
        }

        fetchData().catch((error) => onError(error.message));
        return () => {
            ignore = true;
        };
    }, []);

    return { form, fieldsData, attachesCreateModel, mailRemindersBlockModel };
}
